import React, { createContext, useState, useRef, useContext, useCallback } from 'react';
import * as tus from 'tus-js-client';

const UploadContext = createContext(null);

export const UploadProvider = ({ children, authEndpoint }) => {

    const [queue, setQueue] = useState([]);
    const uploadInstances = useRef({});

    const updateItem = (id, payload) => {
        setQueue(prev => prev.map(item => item.id === id ? { ...item, ...payload } : item));
    };

    const addToQueue = async (file) => {
        const id = Date.now().toString(); // Simple ID generation

        const newItem = {
            id,
            title: file.name,
            fileName: file.name,
            fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            fileType: file.type,
            status: 'AUTHENTICATING', // IDLE, AUTHENTICATING, UPLOADING, COMPLETED, ERROR
            progress: 0,
            videoId: null,
            error: null,
            fileObject: file // Keep reference for retries
        };

        setQueue(prev => [newItem, ...prev]);

        // Start the actual process
        startUploadProcess(id, file);
        return id;
    };

    const startUploadProcess = async (id, file) => {
        if (!authEndpoint) {
            updateItem(id, { status: 'ERROR', error: 'Auth Endpoint missing' });
            return;
        }

        try {
            // 1. Authenticate with your backend
            const token = localStorage.getItem("token");
            const response = await fetch(authEndpoint, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ title: file.name })
            });

            if (!response.ok) throw new Error("Backend authentication failed");
            const data = await response.json();

            updateItem(id, { status: 'UPLOADING', videoId: data.videoId });

            // 2. Configure Tus
            const options = {
                endpoint: "https://video.bunnycdn.com/tusupload",
                retryDelays: [0, 3000, 5000, 10000],
                metadata: {
                    filetype: file.type,
                    title: file.name,
                },
                headers: {
                    AuthorizationSignature: String(data.signature),
                    AuthorizationExpire: String(data.expirationTime),
                    VideoId: String(data.videoId),
                    LibraryId: String(data.videoLibraryId),
                },
                removeFingerprintOnSuccess: true,
                onError: (err) => {
                    console.error("TUS Error:", err);
                    updateItem(id, { status: 'ERROR', error: err.message || "Upload failed" });
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
                    updateItem(id, { progress: Number(percentage) });
                },
                onSuccess: () => {
                    updateItem(id, { status: 'COMPLETED', progress: 100 });
                    // Cleanup instance reference to free memory
                    delete uploadInstances.current[id];
                }
            };

            // 3. Create and Start Instance
            const upload = new tus.Upload(file, options);
            uploadInstances.current[id] = upload;

            // Check for previous uploads to resume
            const previousUploads = await upload.findPreviousUploads();
            if (previousUploads.length) {
                upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            upload.start();

        } catch (err) {
            console.error(err);
            updateItem(id, { status: 'ERROR', error: err.message });
        }
    };

    const pauseUpload = (id) => {
        const upload = uploadInstances.current[id];
        if (upload) {
            upload.abort();
            updateItem(id, { status: 'PAUSED' });
        }
    };

    const resumeUpload = (id) => {
        const upload = uploadInstances.current[id];
        const item = queue.find(q => q.id === id);

        if (upload) {
            // If instance exists, just start it
            upload.start();
            updateItem(id, { status: 'UPLOADING' });
        } else if (item && item.fileObject) {
            // If instance was lost (e.g. hard refresh), restart process
            updateItem(id, { status: 'AUTHENTICATING', error: null });
            startUploadProcess(id, item.fileObject);
        }
    };

    const removeUpload = (id) => {
        // Abort if running
        if (uploadInstances.current[id]) {
            uploadInstances.current[id].abort();
            delete uploadInstances.current[id];
        }
        setQueue(prev => prev.filter(q => q.id !== id));
    };

    const activeUploads = queue.filter(q => q.status === 'UPLOADING' || q.status === 'AUTHENTICATING').length;

    return (
        <UploadContext.Provider value={{
            queue,
            addToQueue,
            pauseUpload,
            resumeUpload,
            removeUpload,
            activeUploads
        }}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUploads = () => useContext(UploadContext);